import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { CredibilityTable } from "../../../components/PortfolioPerformance/credibility-table";
import { KGovernanceCard } from "../../../components/PortfolioPerformance/k-governance-card";
import { LossDevelopmentSection } from "../../../components/PortfolioPerformance/loss-development-section";
import { Pager } from "../../../components/PortfolioPerformance/pager";
import { Skeleton } from "../../../ui/skeleton";
import { hasTier } from "../../../components/PortfolioPerformance/access-control";
import {
  useCalibrationSegments,
  useKGovernance,
  usePromoteK,
} from "../lib/api/loss-calibration";

/**
 * Loss Calibration -- ported from risk-pricing-engine's own
 * AthenaPricingEngine/pages/Calibration.tsx. Same page, same source tables
 * (see app/repository/loss_calibration.py): Segment Credibility reads
 * annualized_loss_calibiration_office, Loss Development / Validation Ledger
 * read vintage_loss_rate, both owned by risk-pricing-engine and read
 * cross-schema rather than copied.
 */
export default function LossCalibration() {
  const [searchParams] = useSearchParams();
  const segment = searchParams.get("segment") ?? undefined;
  const [sandboxK, setSandboxK] = useState("");

  const governanceQuery = useKGovernance();
  const promote = usePromoteK();

  const parsedSandbox = Number(sandboxK);
  const liveK = governanceQuery.data?.liveK;
  const sandboxActive =
    sandboxK.trim() !== "" &&
    Number.isFinite(parsedSandbox) &&
    parsedSandbox > 0 &&
    parsedSandbox !== liveK;

  // Paged server-side. `offset === null` means "land on the page that holds the
  // ?segment= deep link" (the server resolves it and echoes the real offset back);
  // any k change or manual page turn then takes over with a concrete offset.
  const [offset, setOffset] = useState<number | null>(segment ? null : 0);
  const effectiveK = sandboxActive ? parsedSandbox : liveK;
  const segmentsQuery = useCalibrationSegments({
    offset: offset ?? 0,
    k: effectiveK,
    segment: offset === null ? segment : undefined,
  });
  useEffect(() => {
    if (offset === null && segmentsQuery.data) setOffset(segmentsQuery.data.offset);
  }, [offset, segmentsQuery.data]);
  useEffect(() => {
    // k changed -- the sort order changes with it, so start from page one.
    setOffset((o) => (o === null ? o : 0));
  }, [effectiveK]);

  const rows = segmentsQuery.data?.items ?? [];
  const matchedSegment = segment
    ? rows.find((row) => row.segmentId === segment)?.segmentId
    : undefined;

  useEffect(() => {
    if (!matchedSegment) return;
    const id = `segment-${matchedSegment.replace(/\s+/g, "-").toLowerCase()}`;
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [matchedSegment]);

  const isLoading = segmentsQuery.isLoading || governanceQuery.isLoading;
  const isError = segmentsQuery.isError || governanceQuery.isError;

  return (
    <div>
      <Link to="/portfolio-performance/loss-recovery" className="text-xs text-amber hover:underline">
        ← Back to Loss & Recovery
      </Link>

      <PageHeader title="Loss Calibration" subtitle="How thin segments are pooled, and what k is set to" />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          Could not load calibration data.
        </div>
      ) : isLoading || !governanceQuery.data ? (
        <div className="mt-8 space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <>
          <LossDevelopmentSection />

          {/* This table is what K Governance below sets and what Loss Development above
              evidences, so it reads as supporting detail rather than the page's lead panel. */}
          <Panel
            className="mt-8"
            title="Segment Credibility"
            description={
              sandboxActive
                ? `Sandbox — not live · every Z and blended rate recomputed at k = ${parsedSandbox}`
                : "Sorted by own weight ascending, so the most-blended segments come first."
            }
            right={
              segment && offset !== null && !matchedSegment && !segmentsQuery.isFetching ? (
                <span className="text-xs text-muted-foreground">
                  No calibration row for “{segment}”
                </span>
              ) : null
            }
          >
            <CredibilityTable
              rows={rows}
              highlightSegmentId={matchedSegment}
              sandboxActive={sandboxActive}
            />
            <Pager
              offset={segmentsQuery.data?.offset ?? 0}
              total={segmentsQuery.data?.total ?? 0}
              isFetching={segmentsQuery.isFetching}
              onChange={setOffset}
            />
          </Panel>

          {/* Bottom of the page, on purpose: the governance action for a value everything
              above has already justified. */}
          <KGovernanceCard
            canPromote={hasTier("contributor")}
            governance={governanceQuery.data}
            sandboxK={sandboxK}
            onSandboxKChange={setSandboxK}
            isPromoting={promote.isPending}
            onPromote={async (k, note) => {
              await promote.mutateAsync({ k, note });
              setSandboxK("");
            }}
          />
        </>
      )}
    </div>
  );
}
